import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/lib/supabase";

export interface SupplyProduct {
  id: string;
  name: string;
  description?: string;
  price: number;
  sellingPrice: number;
  returnRate: number;
  raised: number;
  target: number;
  stockQuantity: number;
  type: "trial" | "vip";
  tag: string;
  status?: string;
  liked?: boolean;
  category?: string;
  imageUrl?: string;
}

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const DEFAULT_LIMIT = 12;

function getCacheKey(type?: "trial" | "vip"): string {
  return type ? `shop_products_${type}_cache_v1` : `shop_products_all_cache_v1`;
}

function getCached(
  type?: "trial" | "vip",
): { data: SupplyProduct[]; timestamp: number } | null {
  try {
    const raw = sessionStorage.getItem(getCacheKey(type));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed.data || !parsed.timestamp) return null;
    if (Date.now() - parsed.timestamp > CACHE_TTL) {
      sessionStorage.removeItem(getCacheKey(type));
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function setCached(data: SupplyProduct[], type?: "trial" | "vip") {
  try {
    sessionStorage.setItem(
      getCacheKey(type),
      JSON.stringify({ data, timestamp: Date.now() }),
    );
  } catch {
    // ignore
  }
}

function formatProduct(item: Record<string, unknown>): SupplyProduct {
  return {
    id: item.id as string,
    name: item.name as string,
    description: (item.description as string) || "",
    price: item.price as number,
    sellingPrice:
      (item.selling_price as number) ??
      Math.round(
        (item.price as number) *
          (1 + Number((item.return_rate as number) || 25) / 100),
      ),
    returnRate: Number(item.return_rate) || 25,
    raised: item.raised as number,
    target: item.target as number,
    stockQuantity: (item.stock_quantity as number) || 0,
    type: item.type as "trial" | "vip",
    tag:
      (item.tag as string) ||
      ((item.type as string) === "trial" ? "體驗" : "VIP"),
    status: item.status as string,
    category: (item.category as string) || "未分類",
    imageUrl: (item.image_url as string) || undefined,
  };
}

export function useProducts(type?: "trial" | "vip") {
  const [products, setProducts] = useState<SupplyProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const fetchedRef = useRef(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");

    let query = supabase
      .from("products")
      .select("id, name, description, price, selling_price, return_rate, raised, target, stock_quantity, type, tag, status, category, image_url, created_at")
      .eq("status", "active")
      .order("created_at", { ascending: false })
      .limit(DEFAULT_LIMIT);

    if (type) {
      query = query.eq("type", type);
    }

    const { data, error: fetchError } = await query;

    if (fetchError) {
      setError(`讀取商品失敗：${fetchError.message}`);
    } else {
      const formatted = (data || []).map(formatProduct);
      setProducts(formatted);
      setCached(formatted, type);
    }
    setLoading(false);
  }, [type]);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const cached = getCached(type);
    if (cached) {
      setProducts(cached.data);
      setLoading(false);
      // 背景靜默刷新
      let query = supabase
        .from("products")
        .select("id, name, description, price, selling_price, return_rate, raised, target, stock_quantity, type, tag, status, category, image_url, created_at")
        .eq("status", "active")
        .order("created_at", { ascending: false })
        .limit(DEFAULT_LIMIT);

      if (type) {
        query = query.eq("type", type);
      }

      query.then(({ data, error: fetchError }) => {
        if (fetchError) {
          console.warn("[useProducts] background refresh failed:", fetchError);
          return;
        }
        const formatted = (data || []).map(formatProduct);
        setProducts(formatted);
        setCached(formatted, type);
      });
    } else {
      refresh();
    }
  }, [refresh, type]);

  return { products, loading, error, refresh };
}